import * as React from 'react';
import withStyles from 'react-jss';
import { CssType, ThemeType} from 'src/theming/jssTypes';
import { CloseOutlined } from '@ant-design/icons';
import { Modal, Input, Checkbox, Switch, Select, message, Tabs } from 'src/design-system/components/atoms';
import { Space } from 'antd';
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import { DragIcon } from 'src/components/common/icons/DragIcon';
import classNames from 'classnames';
import { withTranslation } from 'react-i18next';

const Search = Input.Search;
const TabPane = Tabs.TabPane;
const Option = Select.Option;

const autoRefreshOptions = [
  {name: '10 Seconds', value: 10},
  {name: '30 Seconds', value: 30},
  {name: '45 Seconds', value: 45},
  {name: '1 minute', value: 60},
];
const styles = (theme: ThemeType): CssType => ({
  listItemStyle: {
    marginBottom: theme.spacing[2],
    backgroundColor: theme.color.semantic.brand.default.light,
    borderRadius: theme.radius.sm,
    paddingLeft: theme.spacing[2],
    paddingRight: theme.spacing[2],
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.sm,
  },
  mainDiv: {
    height:'350px',
    display: 'flex',
    overflow:'hidden',
    justifyContent: 'space-between',
  },
  leftDiv: {
    width: '48%',
    display: 'flex',
    flexDirection: 'column',
    '& .ant-checkbox-group-item': {
      marginBottom: theme.spacing[3],
    },
    '& .ant-checkbox-group': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  searchStyle: {
  },
  rightDiv: {
    overflow:'auto',
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    padding: theme.spacing[3],
    width: '48%',
    '& ul': {
      padding: theme.spacing[0],
      'list-style-type': 'none',
    },
  },
  checkboxDiv: {
    overflow:'auto',
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    height: '100%',
    padding: theme.spacing[4],
  },
  customHeader: {
    fontWeight: theme.typography.fontWeight.semibold,
    fontSize: theme.typography.fontSize.md,
    height: '10%',
    color: theme.color.semantic.text.primary,
    padding: theme.spacing[1],
    borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    marginBottom: theme.spacing[3],
    display: 'flex',
    justifyContent: 'space-between',
  },
  listStyle: {
    height: '90%',
    overflow: 'auto',
  },
  customRightDiv: {
    width: '60%',
    overflow: 'hidden',
  },
  updateDiv: {
    height:'350px',
    overflow:'hidden',
    paddingTop: theme.spacing[3],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    fontSize: theme.typography.fontSize.sm,
  },
  customPrimaryButton: {
    backgroundColor: theme.color.semantic.brand.default.primary,
    color: theme.color.semantic.text.inverse,
  },
  marginLeft8: {
    marginLeft: theme.spacing[2],
  },
  flexSpaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  width60: {
    width: '60%',
  },
  width40: {
    width: '40%',
  },
  textCenter: {
    textAlign: 'center',
  },
  marginTop8: {
    marginTop: theme.spacing[2],
  },
  searchContainer: {
    padding: theme.spacing[3],
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
  },
  columnCounter: {
    padding: theme.spacing[4],
    color: theme.color.semantic.text.primary,
    fontWeight: theme.typography.fontWeight.bold,
  },
  marginBottom12: {
    marginBottom: theme.spacing[3],
  },
  checkboxLabel: {
    fontWeight: theme.typography.fontWeight.semibold,
    fontSize: theme.typography.fontSize.md,
    color: theme.color.semantic.text.primary,
    marginBottom: theme.spacing[3],
  },
  textCapitalize: {
    textTransform: 'capitalize',
  },
});

class CustomColumnsModalPure extends React.PureComponent<any, any> {

  state: any = {
    isUploading: false,
    items: [], // Contains value or column_id of selected or existing columns
    allOptions : [],
    allFilters: [],
    existingFilters: [],
    allActions: [],
    existingActions: [],
    updates: [],
    enableUpdateForAll: false,
    existingHeaders: [],
    allHeaders: [],
  };

  componentDidMount() {
    // Created mapping to render the checkboxes
    // pretty_name => label
    // column_id => value
    const { showFiltersAndActions, fullFiltersMetadata,
      filtersToShow, fullActionsList, updatesToShow, actionsToShow,
      fullColumnList, columnsToShow, headersToShow, fullHeadersList, t, maxColumnLimit } = this.props;

    const allOptions = fullColumnList.map((ele) => {
      return (this.isColumnDisabled(ele.column_id || ele.id) ?
      { label: t(ele.pretty_name), value: ele.column_id || ele.id, disabled: true } :
      { label: t(ele.pretty_name), value: ele.column_id || ele.id });
    });
    const MAX_COLUMNS_SELECTED = maxColumnLimit;
    const existingColumns = columnsToShow.map((ele) => {
      return (ele.column_id || ele.id);
    });
    this.setState({
      allOptions,
      items:existingColumns.slice(0, MAX_COLUMNS_SELECTED),
    });
    if(showFiltersAndActions){
      this.setState({
        allFilters: this.getAllOptionsList(fullFiltersMetadata),
        existingFilters: this.getExistingOptionsList(filtersToShow),
        allActions: this.getAllOptionsList(fullActionsList),
        existingActions: this.getExistingOptionsList(actionsToShow),
        allHeaders: this.getAllHeadersList(fullHeadersList),
        existingHeaders: this.getExistingOptionsList(headersToShow),
        updates: updatesToShow,
      });
    }
  }
  disabledTripColumns = ['organisation_reference_number', 'status'];
  getAllHeadersList = (headers) => {
    const { t } = this.props;
    return headers.map((ele) => {
      return { label: t(ele.pretty_name), value: ele.id };
    });
  };
  getAllOptionsList = (options) => {
    const { t } = this.props;
    return options.map((ele) => {
      return { label: t(ele.label), value: ele.id };
    });
  };
  getExistingOptionsList = (options) => {
    return options.map(ele => {
      return { value: ele.id, visibleOutside: ele.show_on_main_dashboard };
    });
  };

  isColumnDisabled(column_id) {
    const { disabledColumns = [] } = this.props;
    return column_id === 'reference_number' || disabledColumns.includes(column_id);
  }

  ListItem = (record: any) => {
    const { classes, t } = this.props;
    return (
      <div className={classes.listItemStyle}>
        <div>
        { this.isColumnDisabled(record.record.value) ? null : <DragIcon /> }
        <span className={classes.marginLeft8}>
            {t(this.props.fullColumnList.filter(
              x => (x.column_id || x.id) === record.record.value)[0]?.pretty_name)}
          </span>
        </div>
        { this.isColumnDisabled(record.record.value) ? null :
          <div>
            <CloseOutlined onClick={() => this.handleCrossClick(record.record.value)} />
          </div> }
      </div>
    );
  };

  handleCrossClick = (value) => {
    this.setState({
      items: this.state.items.filter(x => x !== value),
    });
  };
  handleFilterCrossClick = (value) => {
    const { existingFilters } = this.state;
    this.setState({
      existingFilters: existingFilters.filter(x => x.value !== value),
    });
  };
  handleActionCrossClick = (value) => {
    const { existingActions } = this.state;
    this.setState({
      existingActions: existingActions.filter(x => x.value !== value),
    });
  };
  handleHeaderCrossClick = (value) => {
    const { existingHeaders } = this.state;
    this.setState({
      existingHeaders: existingHeaders.filter(x => x.value !== value),
    });
  };

  // onCheckboxChange = (checkedValues) => {
  //   const afterSet = new Set(checkedValues);
  //   if (checkedValues.length > this.state.items.length) {
  //     const union = new Set([...this.state.items, ...checkedValues]);
  //     this.setState({
  //       items: Array.from(union),
  //     });
  //   }else {
  //     const intersection = new Set(
  //       [...this.state.items].filter(x => afterSet.has(x)));
  //     this.setState({
  //       items: Array.from(intersection),
  //     });
  //   }
  // }

  SortableItem = SortableElement((props: any) => <this.ListItem record={props} />);
  SortableList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return this.isColumnDisabled(value) ?
          <this.SortableItem key={`item-${index}`} index={index} value={value} disabled={true}/> :
          <this.SortableItem key={`item-${index}`} index={index} value={value}/>;
        })
        }
      </ul>
    );
  });

  onfilterOrActionLocationChange = (checked, value, itemType) => {
    let options = this.getExistingOptions(itemType);
    options = options.map((ele) => {
      if(value === ele.value){
        ele.visibleOutside = checked;
      }
      return ele;
    });
    this.setExistingOptions(itemType, options);
  };

  getExistingOptions = (itemType) => {
    const { existingFilters, existingActions, existingHeaders } = this.state;
    switch (itemType) {
      case 'filter':
        return existingFilters;
      case 'header':
        return existingHeaders;
      case 'action':
        return existingActions;
      default:
        return [];
    }
  };
  setExistingOptions = (itemType, options) => {
    switch(itemType){
      case 'filter':
        this.setState({ existingFilters: options });
        break;
      case 'action':
        this.setState({ existingActions: options});
        break;
      case 'header':
        this.setState({ existingHeaders: options});
        break;
      default:
        return;
    }
  };
  getFullOptionsList = (itemType) => {
    const { fullFiltersMetadata, fullActionsList, fullHeadersList } = this.props;
    switch (itemType) {
      case 'filter':
        return fullFiltersMetadata;
      case 'action':
        return fullActionsList;
      case 'header':
        return fullHeadersList;
      default:
        return [];
    }
  };
  getAllOptions = (itemType) => {
    const { allFilters, allActions, allHeaders } = this.state;
    switch(itemType){
      case 'filter':
        return allFilters;
      case 'action':
        return allActions;
      case 'header':
        return allHeaders;
      default:
        return [];
    }
  };
  setAllOptions = (itemType, options) => {
    switch(itemType){
      case 'filter':
        this.setState({ allFilters: options });
        break;
      case 'action':
        this.setState({ allActions: options});
        break;
      case 'header':
        this.setState({ allHeaders: options});
        break;
      default:
        return;
    }
  };

  getFilterAndActionListItem = (record, itemType) => {
    const { classes, t } = this.props;
    const allOptions = this.getFullOptionsList(itemType);
    let item = null;
    switch(itemType){
      case 'filter':
        item = record.filterItem.filter;
        break;
      case 'action':
        item = record.actionItem.action;
        break;
      case 'header':
        item = record.headerItem.header;
        break;
      default:
        return;
    }

    const isDisabled = itemType !== 'filter' && this.isHeaderDisabled(item.value);

    return (
      <div className={classes.flexSpaceBetween}>
        <div className={`${classes.listItemStyle} ${classes.width60}`}>
          <div>
            {isDisabled ? null: <DragIcon />}
            <span className={classes.marginLeft8}>
              {t(allOptions.filter(x => x.id === item.value)[0]?.label)}
            </span>
          </div>
          <div>
            {isDisabled ? null:  <CloseOutlined onClick={() => {
              switch(itemType){
                case 'filter':
                  this.handleFilterCrossClick(item.value);
                  break;
                case 'action':
                  this.handleActionCrossClick(item.value);
                  break;
                case 'header':
                  this.handleHeaderCrossClick(item.value);
                  break;
                default:
                  return;
              }
            }} />}
          </div>
        </div>
        <div className={`${classes.width40} ${classes.textCenter}`}>
          <Switch
            size="small"
            checked={item.visibleOutside}
            disabled={isDisabled}
            className={classes.marginTop8}
            onChange={(e) => this.onfilterOrActionLocationChange(e, item.value, itemType)} />
        </div>
      </div>
    );
  };
  isHeaderDisabled(column_id) {
    if(this.disabledTripColumns.includes(column_id))
    {
      return true;
    }
    return false;
  }
  FilterListItem = (record: any) => this.getFilterAndActionListItem(record, 'filter');
  SortableFilterItem = SortableElement((props: any) => {
    return <this.FilterListItem filterItem={props} />;
  });
  SortableFilterList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return <this.SortableFilterItem key={`item-${index}`} index={index} filter={value}/>;
        })}
      </ul>
    );
  });

  ActionListItem = (record: any) => this.getFilterAndActionListItem(record, 'action');
  SortableActionItem = SortableElement((props: any) => {
    return <this.ActionListItem actionItem={props} />;
  });
  SortableActionList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return <this.SortableActionItem key={`item-${index}`} index={index} action={value}/>;
        })}
      </ul>
    );
  });

  HeaderListItem = (record: any) => this.getFilterAndActionListItem(record, 'header');
  SortableHeaderItem = SortableElement((props: any) => {
    return <this.HeaderListItem headerItem={props} />;
  });
  SortableHeaderList = SortableContainer((props: any) => {
    return (
      <ul>
        {props.items.map((value, index) => {
          return this.isHeaderDisabled(value?.value) ? <this.SortableHeaderItem key={`item-${index}`} index={index} header={value} disabled={true}/>
            : <this.SortableHeaderItem key={`item-${index}`} index={index} header={value}/>;
        })}
      </ul>
    );
  });

  onSortEnd = ({ oldIndex, newIndex }) => {
    if (newIndex !== 0) {
      this.setState(({ items }) => ({
        items: arrayMove(items, oldIndex, newIndex),
      }));
    }
  };

  onFilterSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ existingFilters }) => ({
      existingFilters: arrayMove(existingFilters, oldIndex, newIndex),
    }));
  };

  onActionSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ existingActions }) => ({
      existingActions: arrayMove(existingActions, oldIndex, newIndex),
    }));
  };

  onHeaderSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(({ existingHeaders }) => ({
      existingHeaders: arrayMove(existingHeaders, oldIndex, newIndex),
    }));
  };

  getCustomOptionsList = (options) => {
    return options.map((ele) => {
      return { id: ele.value, show_on_main_dashboard: ele.visibleOutside };
    });
  };

  handleModalOk = async () => {
    const { fullColumnList, onModalSubmit, minColumnLimit = 1, t } = this.props;
    const { items, existingFilters, existingActions, updates,  enableUpdateForAll, existingHeaders } = this.state;

    if (items.length < minColumnLimit) {
      message.error(`You need to select at least ${minColumnLimit} columns`);
      return;
    }

    const columns = items.map((element) => {
      return fullColumnList.filter(x => ((x.column_id || x.id) === element))[0];
    }).filter(col => col);

    const customHeaders = this.getCustomOptionsList(existingHeaders);
    const customFilters = this.getCustomOptionsList(existingFilters);
    const customActions = this.getCustomOptionsList(existingActions);

    await onModalSubmit({
      columns, updates, customActions, customHeaders, customFilters, enableUpdateForAll
    });

    this.handleModalClose();
  };

  handleModalClose = () => {
    this.props.onModalClose();
  };

  handleSearchChange = (e) => {
    const { t } = this.props;
    this.setState({
      allOptions: this.props.fullColumnList
        .filter(x =>
          x.pretty_name
            .toLowerCase()
            .includes(e.target.value.toLowerCase()),
        )
        .map((ele) => {
          return ( this.isColumnDisabled(ele.column_id || ele.id) ?
            { label: t(ele.pretty_name), value: ele.column_id || ele.id, disabled: true } :
            { label: t(ele.pretty_name), value: ele.column_id || ele.id });
        },
        ),
    });
  };

  onFilterOrActionSearch = (e, searchType) => {
    const optionsList = this.getFullOptionsList(searchType);
    const searchedOptions = optionsList.filter(x => {
      return x.label?.toLowerCase()?.includes(e.target.value.toLowerCase());
    }).map(ele => {
      return { label: ele.label, value: ele.id };
    });
    this.setAllOptions(searchType, searchedOptions);
  };
  handleCheckboxChange = (e) => {
    if (e.target.checked) {
      this.setState({ items: [...this.state.items, e.target.value] });
    } else {
      const items = this.state.items.filter(x => x !== e.target.value);
      this.setState({ items });
    }
  };
  onFilterOrActionCheckboxChange = (e, searchType) => {
    const existingOptions = this.getExistingOptions(searchType);
    if (e.target.checked) {
      this.setExistingOptions(searchType, [...existingOptions, { value: e.target.value }]);
    } else {
      const optionsToShow = existingOptions.filter(x => x.value !== e.target.value);
      this.setExistingOptions(searchType, optionsToShow);
    }
  };
  renderColumns = () => {
    const { classes,t, maxColumnLimit } = this.props;
    const MAX_COLUMNS_SELECTED = maxColumnLimit;
    if (this.state.items?.length > MAX_COLUMNS_SELECTED) {
      this.setState({
        items: this.state.items?.slice(0, MAX_COLUMNS_SELECTED),
      });
    }
    let totalCheckedColumns = 0;
    return (
      <div className={classes.mainDiv}>
        <div className={classes.leftDiv} >
          <div className={classes.searchContainer}>
            <Search
              onChange={this.handleSearchChange}
              className={classes.searchStyle}
              placeholder={t('search')}
            />
          </div>
          <div className={classes.columnCounter}>
            {this.state.items?.length || 0} {t('Selected / Max')} {MAX_COLUMNS_SELECTED} {t('columns allowed')}
          </div>
          <div className={classes.checkboxDiv}>
            {this.state.allOptions.map((x) => {
              let isChecked = this.state.items.includes(x.value);
              let shouldDisable = x.disabled || false;
              if (this.state.items?.length >= MAX_COLUMNS_SELECTED && !isChecked) {
                shouldDisable = true;
              }
              if (totalCheckedColumns >= MAX_COLUMNS_SELECTED) {
                isChecked = false;
                shouldDisable = true;
              }
              if(isChecked){
                totalCheckedColumns++;
              }
              return (
                <div className={classes.marginBottom12} key={x.value}>
                  <Checkbox
                    checked = {isChecked}
                    value={x.value}
                    disabled={shouldDisable}
                    onChange={(e) => { this.handleCheckboxChange(e); }}
                  >
                    {t(x.label)}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
        <div className={classes.rightDiv} >
          <div className={classes.checkboxLabel}>
            {this.props.t('existing_columns')}
          </div>
          <div><this.SortableList distance={1} items={this.state.items} onSortEnd={this.onSortEnd}
            helperClass="sortableHelper" />
            </div>
        </div>
      </div>
    );
  };
  renderFiltersAndActions = (itemType) => {
    const { classes, t } = this.props;
    const { existingFilters, existingActions, existingHeaders } = this.state;
    const allOptions = this.getAllOptions(itemType);
    const existingOptions = this.getExistingOptions(itemType);
    return (
      <div className={classes.mainDiv}>
        <div className={`${classes.leftDiv} ${classes.width40}`} >
          <div className={classes.searchContainer}>
            <Search
              onChange={(e) => this.onFilterOrActionSearch(e, itemType)}
              className={classes.searchStyle}
              placeholder={t('filter_search')}
            />
          </div>
          <div className={classes.checkboxDiv}>
            {allOptions.map((x) => {
              const isDisabled = itemType !== 'filter' && this.isHeaderDisabled(x.value);
              return (
                <div className={classes.marginBottom12}>
                  <Checkbox
                    checked = {existingOptions.findIndex(ele => ele.value === x.value) !== -1}
                    value={x.value}
                    disabled = {isDisabled}
                    onChange={(e) => this.onFilterOrActionCheckboxChange(e, itemType)} >
                      {t(x.label)}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
        <div className={classNames(classes.rightDiv, classes.customRightDiv)} >
          <div className={classes.customHeader} >
            <div className={classes.textCapitalize}>{itemType === 'header' ? t('details_to_show') : t(`${itemType}s_to_show`) }</div>
            <div>{t('show_on_main_dashboard')}</div>
          </div>
          <div className={classes.listStyle}>
            {
              {
                filter : <this.SortableFilterList distance={1} items={existingFilters}
                onSortEnd={this.onFilterSortEnd} helperClass="sortableHelper" />,
                action : <this.SortableActionList distance={1} items={existingActions}
                onSortEnd={this.onActionSortEnd} helperClass="sortableHelper" />,
                header : <this.SortableHeaderList distance={1} items={existingHeaders}
                onSortEnd={this.onHeaderSortEnd} helperClass="sortableHelper" />,
              }[itemType]
            }
          </div>
        </div>
      </div>
    );
  };
  generateOptionsFromObjectList = (list) => {
    const optionsList: any = [];
    if (list) {
      list.forEach((element) => {
        optionsList.push(<Option value={element.value}>
          {element.name}</Option>);
      });
    }
    return optionsList;
  };
  changeUpdateValue = (val, object, key) => {
    const { updatesActive = [{}] } = this.props;
    const defaultUpdateValues = updatesActive.filter(elem => elem.id === object.id);
    const update = {
     ...defaultUpdateValues[0],
      ...object,
    };
    update[key] = val;
    const items  = this.state.updates.filter(elem => elem.id !== object.id);
    this.setState({ updates: [...items, update]});
  };
  renderUpdates = () => {
    const { classes, showEnableUpdatesForAll, t } = this.props;
    const { updates } = this.state;
    return (
      <div className={classes.updateDiv}>
        <div>{updates.map((x) => {
          return (
            this.renderUpdate(x)
          );
        })}</div>
        <div>
          <Space>
          <Checkbox
            onChange={(e) => this.setState({enableUpdateForAll: e.target.checked})}>
          </Checkbox>
          {showEnableUpdatesForAll && <span>{t('enable_this_setting_on_all_tabs')}</span>}
          </Space>
        </div>
      </div>
    );
  };
  renderUpdate = (update) => {
    const { updatesActive, t } = this.props;
    const activeUpdate = updatesActive.find(element => element.id === update.id);
    switch (update.id) {
      case 'auto_refresh_duration_in_sec':
        return <Space>
          <span>{t(update.label)}</span>
          <Switch
            onChange={(e) => this.changeUpdateValue(e, update, 'isActive')}
            defaultChecked={activeUpdate?.isActive} size="small"
           />
          <Select
            defaultValue={activeUpdate?.value}
            onChange={(e) => this.changeUpdateValue(e, update, 'value')}
            placeholder={t('select_duration')}
          >
            {this.generateOptionsFromObjectList(autoRefreshOptions)}
          </Select>
        </Space>;
      default:
        return null;
    }
  };
  render() {
    const { isVisible, classes, showFiltersAndActions, defaultTab, hideActions,
      updatesToShow, hideFilters, showHeaders, hideColumnTab, t } = this.props;
    const { isUploading , updates} = this.state;
    return (
      <Modal
        title={showFiltersAndActions ? t('customise') : t('customize_columns')}
        visible={isVisible}
        okText={t('update')}
        cancelText={t('cancel')}
        destroyOnClose maskClosable={false}
        width={showFiltersAndActions ? 900 : 800}
        confirmLoading={isUploading}
        onOk={this.handleModalOk}
        onCancel={this.handleModalClose}
        okButtonProps={{ className: classes.customPrimaryButton }}
      >
        {showFiltersAndActions ?
          <Tabs defaultActiveKey={defaultTab || 'column'}>
            {  !hideColumnTab &&
              <TabPane tab={t('columns')} key="column">
                {this.renderColumns()}
              </TabPane>
            }
            {!hideFilters &&
              <TabPane tab={t('filter')} key="filter">
                {this.renderFiltersAndActions('filter')}
              </TabPane>
            }
            {!hideActions &&
              <TabPane tab={t('actions')} key="action">
                {this.renderFiltersAndActions('action')}
              </TabPane>
            }
            {showHeaders &&
              <TabPane tab={t('details')} key="header">
                {this.renderFiltersAndActions('header')}
              </TabPane>
            }
            {updatesToShow.length && <TabPane tab="Updates" key="updates">
                {this.renderUpdates()}
              </TabPane>}
          </Tabs> : this.renderColumns()}
      </Modal>
    );
  }
}

const StyledCustomModal = withStyles(styles, { injectTheme: true })(CustomColumnsModalPure);
export default withTranslation('translation')(StyledCustomModal);
